import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Typography,
  Chip,
  Box,
  Grid,
  Button,
  Paper,
} from '@mui/material';
import { styled, alpha } from '@mui/material/styles';
import { colors } from '../../../styles/theme';
export const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    borderRadius: theme.spacing(1.5),
    overflow: 'hidden',
  },
}));

export const StyledDialogTitle = styled(DialogTitle)(({ theme }) => ({
  padding: `${theme.spacing(2)} ${theme.spacing(3)}`,
  borderBottom: `1px solid ${theme.palette.divider}`,
  background: 'linear-gradient(to right, #06b6d4, #10b981)',
  color: '#ffffff',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  fontWeight: 600,
}));

export const TitleContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
}));

export const CloseButton = styled(IconButton)(({ theme }) => ({
  color: '#ffffff',
  '&:hover': {
    color: alpha(theme.palette.common.white, 0.85),
  },
}));

export const StyledDialogContent = styled(DialogContent)(({ theme }) => ({
  padding: 0,
}));

export const ContentContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
}));

export const HeaderSection = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: theme.spacing(3),
}));

export const ProductInfoContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(2),
}));

export const ImageContainer = styled(Box)(({ theme }) => ({
  position: 'relative',
  '&:hover': {
    cursor: 'pointer',
    transform: 'scale(1.02)',
    transition: 'transform 0.2s ease-in-out',
  },
}));

export const ProductImage = styled('img')(({ theme }) => ({
  width: '96px',
  height: '96px',
  objectFit: 'cover',
  borderRadius: theme.spacing(1),
  border: `1px solid ${theme.palette.divider}`,
  boxShadow: theme.palette.mode === 'dark' ? 'none' : '0 2px 8px rgba(0, 0, 0, 0.1)',
}));

export const ProductDetails = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
}));

export const ProductName = styled(Typography)(({ theme }) => ({
  fontWeight: 700,
  color: theme.palette.text.primary,
  fontSize: '1.5rem',
  marginBottom: theme.spacing(0.5),
}));

export const SkuContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  marginTop: theme.spacing(0.5),
}));

export const SkuLabel = styled(Typography)(({ theme }) => ({
  fontSize: '0.875rem',
  color: theme.palette.text.secondary,
  marginRight: theme.spacing(1),
}));

export const SkuValue = styled(Typography)(({ theme }) => ({
  fontSize: '0.875rem',
  color: theme.palette.text.secondary,
}));

export const StatusChip = styled(Chip)(({ theme }) => ({
  padding: `${theme.spacing(0.5)} ${theme.spacing(1.5)}`,
  fontWeight: 500,
  fontSize: '0.875rem',
  '&.success': {
    backgroundColor: colors.success.light + '20',
    color: colors.success.dark,
  },
  '&.error': {
    backgroundColor: colors.error.light + '20',
    color: colors.error.dark,
  },
}));

export const InfoCard = styled(Paper)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  borderRadius: theme.spacing(1.5),
  ...(theme.palette.mode === 'dark'
    ? {
        boxShadow: 'none',
        border: `1px solid ${theme.palette.divider}`,
      }
    : {
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
        border: `1px solid ${colors.grey[200]}`,
      }),
  marginBottom: theme.spacing(2),
  overflow: 'hidden',
}));

export const CardHeader = styled(Box)(({ theme }) => ({
  padding: `${theme.spacing(1)} ${theme.spacing(2)}`,
  borderBottom: `1px solid ${theme.palette.divider}`,
  display: 'flex',
  alignItems: 'center',
}));

export const CardTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 600,
  fontSize: '0.875rem',
  display: 'flex',
  alignItems: 'center',
}));

export const CardContent = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
}));

export const InfoGrid = styled(Grid)(({ theme }) => ({
  marginBottom: theme.spacing(1),
}));

export const InfoLabel = styled(Typography)(({ theme }) => ({
  fontSize: '0.75rem',
  color: theme.palette.text.secondary,
  marginBottom: theme.spacing(0.5),
}));

export const InfoValue = styled(Typography)(({ theme }) => ({
  fontSize: '0.875rem',
  fontWeight: 500,
  color: theme.palette.text.primary,
}));

export const DescriptionText = styled(Typography)(({ theme }) => ({
  fontSize: '0.875rem',
  color: theme.palette.text.primary,
  lineHeight: 1.5,
}));

export const TagsContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  gap: theme.spacing(1),
  marginTop: theme.spacing(0.5),
}));

export const TagChip = styled(Chip)(({ theme }) => ({
  backgroundColor: colors.info.light + '20',
  color: colors.info.dark,
  fontSize: '0.75rem',
  height: '24px',
}));

export const NoTagsText = styled(Typography)(({ theme }) => ({
  fontSize: '0.875rem',
  color: theme.palette.text.secondary,
  fontStyle: 'italic',
}));

export const PriceText = styled(Typography)(({ theme }) => ({
  fontSize: '1.25rem',
  fontWeight: 700,
  color: colors.success.main,
}));

export const StyledDialogActions = styled(DialogActions)(({ theme }) => ({
  backgroundColor:
    theme.palette.mode === 'dark' ? theme.palette.background.default : colors.grey[50],
  padding: `${theme.spacing(2)} ${theme.spacing(3)}`,
  borderTop: `1px solid ${theme.palette.divider}`,
  display: 'flex',
  justifyContent: 'flex-end',
  gap: theme.spacing(1.5),
}));

export const CloseButtonStyled = styled(Button)(({ theme }) => ({
  minWidth: '120px',
  padding: '9px 18px',
  borderRadius: '10px',
  textTransform: 'none',
  fontWeight: 500,
  fontSize: '0.875rem',
  color: theme.palette.text.secondary,
  backgroundColor: theme.palette.mode === 'dark' ? theme.palette.action.hover : colors.grey[50],
  border: 'none',
  '&:hover': {
    backgroundColor:
      theme.palette.mode === 'dark' ? alpha(theme.palette.common.white, 0.12) : colors.grey[100],
    color: theme.palette.text.primary,
  },
}));

export const EditButtonStyled = styled(Button)(({ theme }) => ({
  padding: `${theme.spacing(1)} ${theme.spacing(2)}`,
  background: 'linear-gradient(to right, #06b6d4, #10b981)',
  color: '#ffffff',
  borderRadius: theme.spacing(1),
  fontWeight: 500,
  fontSize: '0.875rem',
  textTransform: 'none',
  display: 'flex',
  alignItems: 'center',
  '&:hover': {
    background: 'linear-gradient(to right, #0891b2, #059669)',
  },
}));

export const IconWrapper = styled('span')(({ theme }) => ({
  marginRight: theme.spacing(1),
  display: 'flex',
  alignItems: 'center',
}));
